import { useMemo } from 'react';
import { TabItem } from '../../components/components.interfaces';
import AcademicsLibrary from './subviews/academicsLibrary/academicsLibrary.view';
import AcademicsAssessments from './subviews/academicsAssessments/academicsAssessments.view';
import AcademicsClassesAndCourses from './subviews/academicsClassesAndCourses/academicsClassesAndCourses.view';
import AcademicsMaterialsView from './subviews/academicsMaterials/academicsMaterials.view';
import AcademicsSchedules from './subviews/academicsSchedules/academicsSchedules.view';
import { AcademicsClassesAndCoursesProvider } from './contexts/academicsClassesAndCourses.context';
import { AcademicsSchedulesProvider } from './contexts/academicsSchedules.context';
import { AcademicsMaterialsProvider } from './contexts/academicsMaterials.context';
import { AcademicsAssessmentsProvider } from './contexts/academicsAssessments.context';
import { AcademicsLibraryProvider } from './contexts/academicsLibrary.context';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import AcademicsLessonPlansView from './subviews/academicsLessonPlans/academicsLessonPlans.view';
import { AcademicsLessonPlansProvider } from './contexts/academicsLessonPlans.context';
import { useRbacState } from 'src/context/rbac.context';
import { hasReadPermission } from 'src/utils/rbacHelpers';

export const academicsSubRouteIds = [
  'curriculum',
  'schedules',
  'lesson-plans',
  'materials',
  'assessments',
  'library',
];

export const useAcademicsTabMenuItems = (
  translateText: TTranslateTextType
): TabItem[] => {
  const { rbac } = useRbacState();

  return useMemo(() => {
    const tabs: TabItem[] = [];

    if (hasReadPermission(rbac, 'academics', 'curriculum')) {
      tabs.push({
        label: translateText('academics.tabs.classesAndCourses'),
        onClickHandler: () => {},
        tabPanel: (
          <AcademicsClassesAndCoursesProvider>
            <AcademicsClassesAndCourses />
          </AcademicsClassesAndCoursesProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'academics', 'schedules')) {
      tabs.push({
        label: translateText('academics.tabs.schedules'),
        onClickHandler: () => {},
        tabPanel: (
          <AcademicsSchedulesProvider>
            <AcademicsClassesAndCoursesProvider>
              <AcademicsSchedules />
            </AcademicsClassesAndCoursesProvider>
          </AcademicsSchedulesProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'academics', 'lessonPlans')) {
      tabs.push({
        label: translateText('academics.tabs.lessonplans'),
        onClickHandler: () => {},
        tabPanel: (
          <AcademicsClassesAndCoursesProvider>
            <AcademicsLessonPlansProvider>
              <AcademicsLessonPlansView />
            </AcademicsLessonPlansProvider>
          </AcademicsClassesAndCoursesProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'academics', 'materials')) {
      tabs.push({
        label: translateText('academics.tabs.materials'),
        onClickHandler: () => {},
        tabPanel: (
          <AcademicsMaterialsProvider>
            <AcademicsMaterialsView />
          </AcademicsMaterialsProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'academics', 'assessments')) {
      tabs.push({
        label: translateText('academics.tabs.assessments'),
        onClickHandler: () => {},
        tabPanel: (
          <AcademicsClassesAndCoursesProvider>
            <AcademicsAssessmentsProvider>
              <AcademicsAssessments />
            </AcademicsAssessmentsProvider>
          </AcademicsClassesAndCoursesProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'academics', 'library')) {
      tabs.push({
        label: translateText('academics.tabs.library'),
        onClickHandler: () => {},
        tabPanel: (
          <AcademicsLibraryProvider>
            <AcademicsLibrary />
          </AcademicsLibraryProvider>
        ),
      });
    }

    return tabs;
  }, [rbac, translateText]);
};

/** Returns route IDs for tabs the user has read permission for, in display order */
export const useAcademicsAvailableRouteIds = (): string[] => {
  const { rbac } = useRbacState();
  return useMemo(() => {
    const routes: string[] = [];
    if (hasReadPermission(rbac, 'academics', 'curriculum')) routes.push('curriculum');
    if (hasReadPermission(rbac, 'academics', 'schedules')) routes.push('schedules');
    if (hasReadPermission(rbac, 'academics', 'lessonPlans')) routes.push('lesson-plans');
    if (hasReadPermission(rbac, 'academics', 'materials')) routes.push('materials');
    if (hasReadPermission(rbac, 'academics', 'assessments')) routes.push('assessments');
    if (hasReadPermission(rbac, 'academics', 'library')) routes.push('library');
    return routes;
  }, [rbac]);
};

export const getAcademicsTabIndex = (pathname: string) => {
  const academicsTabsIndexObject = {
    curriculum: 0,
    schedules: 1,
    'lesson-plans': 2,
    materials: 3,
    assessments: 4,
    library: 5,
  };

  return (
    academicsTabsIndexObject[
      pathname as keyof typeof academicsTabsIndexObject
    ] ?? 0
  );
};

export const getAcademicsSubRouteId = (index: number) =>
  academicsSubRouteIds[index];
